import { useMemo, ReactNode } from 'react';

import { useToken } from 'src/features/auth';
import type { User } from 'src/features/auth';
import { useGetPermissions } from 'src/features/auth/api/useGetPermissions';
import AuthContext from 'src/features/auth/context/AuthContext';
import { useParking } from 'src/providers/ParkingProvider';
import { SettingsProvider } from 'src/providers/SettingsProvider';
import { useGetUser } from 'src/features/profile/api/useGetUser';

function AuthProvider({ children }: { children: ReactNode }) {
    const { data: tokenData, isPending: isTokenPending } = useToken();
    const { parkingId } = useParking();
    const { data: permissions, isLoading: isPermissionsLoading } = useGetPermissions({
        config: { enabled: !!tokenData },
        parking: parkingId,
    });

    const { data: user } = useGetUser({
        config: { enabled: !!tokenData },
        userId: tokenData ? tokenData.sub ?? tokenData.id : '',
    });

    const value = useMemo(
        () => ({
            user: user as User,
            permissions: permissions || [],
            permissionsLoaded: !isTokenPending && !isPermissionsLoading,
        }),
        [user, permissions, isTokenPending, isPermissionsLoading],
    );

    return (
        <AuthContext.Provider value={value}>
            <SettingsProvider>{children}</SettingsProvider>
        </AuthContext.Provider>
    );
}

export { AuthProvider };
